import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { ShieldCheck } from "lucide-react";

export default function AdminSetup() {
  const nav = useNavigate();
  const [checking, setChecking] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("Admin");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/setup/status`).then((r) => {
      setDisabled(!!r.data.initialized);
    }).catch(() => {}).finally(() => setChecking(false));
  }, []);

  const submit = async () => {
    if (pwd !== pwd2) return toast.error("Passwords do not match");
    if (pwd.length < 10 || !/[A-Z]/.test(pwd) || !/[a-z]/.test(pwd) || !/\d/.test(pwd))
      return toast.error("Password must be 10+ chars with upper, lower and a number");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/setup`, { email: email.trim().toLowerCase(), name: name.trim(), password: pwd });
      toast.success("Admin account created. Please sign in.");
      nav("/admin/login");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Setup failed");
    } finally {
      setBusy(false);
    }
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center text-[#6F6B66]">…</div>;

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="admin-setup">
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">KT Milano · Admin setup</div>
        <h1 className="font-serif text-3xl text-[#383633] tracking-tight mb-2">
          {disabled ? "Setup already complete" : "Create the first administrator"}
        </h1>
        <p className="text-sm text-[#6F6B66] mb-8 leading-relaxed">
          {disabled
            ? "An administrator already exists. This one-time setup page is now disabled for security."
            : "This page is available only until the first admin is created. It will disable itself automatically afterwards."}
        </p>
        {disabled ? (
          <a href="/admin/login" className="inline-flex items-center gap-2 rounded-full bg-[#383633] text-white px-6 py-3 text-xs kt-tracking uppercase">
            Go to sign in →
          </a>
        ) : (
          <div className="space-y-4 bg-white border kt-line rounded-2xl p-6 kt-shadow">
            <div>
              <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Admin name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-11 border-[#ECE7DF]" data-testid="setup-name" />
            </div>
            <div>
              <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-11 border-[#ECE7DF]" data-testid="setup-email" />
            </div>
            <div>
              <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Password</Label>
              <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-2 h-11 border-[#ECE7DF]" data-testid="setup-password" />
              <p className="text-[11px] text-[#6F6B66] mt-1 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Min 10 chars, incl. upper, lower and a number.</p>
            </div>
            <div>
              <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Confirm password</Label>
              <Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="mt-2 h-11 border-[#ECE7DF]" data-testid="setup-password-confirm" />
            </div>
            <Button
              onClick={submit}
              disabled={busy || !email || !pwd || !pwd2}
              data-testid="setup-submit"
              className="w-full rounded-full h-12 bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase"
            >
              {busy ? "Creating…" : "Create administrator"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
